<script setup>
/**
 * "You can change these" (XN-4561): the fields a policyholder owns, in the
 * prototype's two-column grid (one on a phone). Save sends only what differs
 * from what was loaded; with nothing changed it is aria-disabled and sends
 * nothing. A refused field says why under itself and takes the focus; any
 * other failure keeps the typing. An email change is confirmed at the new
 * address, so the field goes on showing the current one, and the line about
 * the pending change sits in the `email-note` slot, which the page fills.
 * Prototype `profile()`, build 30 Sep 12:40.
 */
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue';
import XlButton from '../xl/XlButton.vue';
import XlField from '../xl/XlField.vue';
import XlTag from '../xl/XlTag.vue';
import IconCheck from '../icons/IconCheck.vue';
import { updateMyDetails } from '../../api/portal';

const FIELDS = ['first_name', 'last_name', 'phone', 'email', 'address'];
const SAVED_MS = 2200;
const FAILED = "Couldn't save — try again.";

const props = defineProps({
  me: { type: Object, required: true },
  // Who changes a policy's address: the broker's name, "your broker", or the hub.
  askWho: { type: String, required: true },
});
const emit = defineEmits(['saved']);

const pick = (me) => Object.fromEntries(FIELDS.map((field) => [field, me[field] ?? '']));
// Spaces round a value change nothing, and nor does an email's case: the
// server trims, and compares emails case-blind before asking SSO for a change.
const same = (field, a, b) => {
  const [x, y] = [String(a ?? '').trim(), String(b ?? '').trim()];
  return field === 'email' ? x.toLowerCase() === y.toLowerCase() : x === y;
};

const root = ref(null);
const draft = ref(pick(props.me));
const errors = ref({});
let base = pick(props.me); // what the draft was last measured against
// Take the server's value for each field the person hasn't touched since `was`; keep their typing in the rest.
function follow(me, was) {
  const next = pick(me);
  for (const field of FIELDS) if (draft.value[field] === was[field]) draft.value[field] = next[field];
  base = next;
}
watch(() => props.me, (me) => follow(me, base));
const state = ref('idle'); // idle | saving | saved | failed
const failure = ref(FAILED); // the server's own words when it gave some (a 409 names the hub)
const changed = computed(() => FIELDS.filter((field) => !same(field, draft.value[field], props.me[field])));
const blocked = computed(() => state.value === 'saving' || !changed.value.length);
// XN-4688: the page asks before leaving while this is true; the one source of "changed".
defineExpose({ unsaved: computed(() => changed.value.length > 0) });
const hint = computed(() => `This is where you live. Your home policy address is set separately — ask ${props.askWho} to change that.`);

let savedTimer = null;
function settle(next) {
  clearTimeout(savedTimer);
  state.value = next;
  if (next === 'saved') savedTimer = setTimeout(() => { state.value = 'idle'; }, SAVED_MS);
}
onBeforeUnmount(() => clearTimeout(savedTimer));

async function focusFirstError() {
  await nextTick();
  root.value?.querySelector('[aria-invalid="true"]')?.focus();
}

async function save() {
  if (blocked.value) return;
  const body = Object.fromEntries(changed.value.map((field) => [field, draft.value[field]]));
  const sent = { ...draft.value };
  errors.value = {};
  settle('saving');

  try {
    const { errors: refused, ...fresh } = await updateMyDetails(body);
    const emailError = refused?.email?.[0] ?? '';
    emit('saved', fresh);
    // What the server now holds — except an address it refused, which stays as typed beside its reason.
    follow(fresh.me, emailError ? { ...sent, email: undefined } : sent);
    if (!emailError) {
      settle('saved');
      return;
    }
    errors.value = { email: emailError };
    // "Saved" only when something was: an email-only change that was refused saved nothing.
    settle(Object.keys(body).some((field) => field !== 'email') ? 'saved' : 'idle');
    await focusFirstError();
  } catch (error) {
    const refused = error?.response?.status === 422 ? error.response.data?.errors : null;
    if (!Object.keys(refused ?? {}).some((field) => FIELDS.includes(field))) {
      failure.value = (error?.response?.status === 409 && error.response.data?.message) || FAILED;
      settle('failed');
      return;
    }
    errors.value = Object.fromEntries(Object.entries(refused).filter(([field]) => FIELDS.includes(field)).map(([field, messages]) => [field, messages[0]]));
    settle('idle');
    await focusFirstError();
  }
}
</script>

<template>
  <section ref="root" class="pdet-form" data-testid="details-card-form">
    <div class="pdet-form__sh"><h2>You can change these</h2><i class="pdet-form__ln" /></div>

    <form class="pdet-form__card" novalidate @submit.prevent="save">
      <div class="pdet-form__grid">
        <div class="pdet-form__fld">
          <XlField v-model="draft.first_name" label="First name" name="first_name" aria-required="true" autocomplete="given-name" maxlength="50" :counted="false" :error="errors.first_name || ''" />
        </div>
        <div class="pdet-form__fld">
          <XlField v-model="draft.last_name" label="Last name" name="last_name" aria-required="true" autocomplete="family-name" maxlength="50" :counted="false" :error="errors.last_name || ''" />
        </div>
        <div class="pdet-form__fld">
          <XlField v-model="draft.phone" type="tel" label="Phone number" name="phone" autocomplete="tel" maxlength="50" :counted="false" :error="errors.phone || ''" />
        </div>
        <div class="pdet-form__fld">
          <XlField v-model="draft.email" type="email" label="Email address" name="email" aria-required="true" autocomplete="email" maxlength="255" :counted="false" :error="errors.email || ''" />
          <!-- An email change waiting to be confirmed is shown here; the page fills it. -->
          <slot name="email-note" />
        </div>
      </div>

      <div class="pdet-form__fld">
        <label class="pdet-form__label" for="address">Address <span>— optional</span></label>
        <XlField v-model="draft.address" name="address" autocomplete="street-address" maxlength="255" :counted="false" :hint="hint" :error="errors.address || ''" />
      </div>

      <div class="pdet-form__actions">
        <XlButton type="submit" data-testid="details-save" :aria-disabled="blocked ? 'true' : undefined">
          {{ state === 'saving' ? 'Saving…' : 'Save my details' }}
        </XlButton>
        <span class="pdet-form__status" role="status" data-testid="details-save-status">
          <XlTag v-if="state === 'saved'" tone="success"><IconCheck color="currentColor" size="12" /> Saved</XlTag>
          <template v-else-if="state === 'failed'">{{ failure }}</template>
        </span>
      </div>
    </form>
  </section>
</template>

<style scoped>
/* Measurements from the prototype's profile() form, .grid2, .fld and .inp, build 30 Sep 12:40. */
.pdet-form__sh { display: flex; align-items: baseline; gap: 12px; margin: 34px 0 14px; }
.pdet-form__sh h2 { margin: 0; font-size: 13px; font-weight: 600; letter-spacing: 0.1em; text-transform: uppercase; color: var(--xl-color-outline-variant); }
.pdet-form__ln { flex: 1; height: 1px; background: var(--xl-color-border); }
.pdet-form__card {
  padding: 22px 24px;
  margin: 0 0 14px;
  background: #fff;
  border: 1px solid var(--xl-color-border);
  border-radius: var(--xl-radius-lg);
  box-shadow: var(--xl-shadow-1);
}
.pdet-form__grid { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
.pdet-form__fld { margin-bottom: 16px; min-width: 0; }
.pdet-form__label { display: block; margin-bottom: 6px; font-size: 12.5px; font-weight: 500; color: var(--xl-color-on-surface-variant); }
.pdet-form__label span { font-weight: 400; color: var(--xl-color-outline-variant); }
/* XlField's own label, box and note, at the prototype's .fld/.inp sizes. */
.pdet-form :deep(.xl-field__label) { font-size: 12.5px; line-height: normal; color: var(--xl-color-on-surface-variant); }
.pdet-form :deep(.xl-field__control) { border-radius: 11px; padding: 0 14px; }
.pdet-form :deep(.xl-field__note) { font-size: 12.5px; }
.pdet-form__actions { display: flex; gap: 10px; align-items: center; flex-wrap: wrap; margin-top: 6px; }
.pdet-form__actions :deep(.xl-btn[aria-disabled="true"]) { opacity: 0.5; cursor: not-allowed; transform: none; box-shadow: none; }
.pdet-form__status { font-size: 13.5px; color: var(--xl-color-error); }
@media (max-width: 480px) {
  .pdet-form__grid { grid-template-columns: 1fr; }
  .pdet-form__card { padding: 18px; }
}
</style>
